/**
 * ImageLightbox — full-size view of one image over a dark backdrop.
 *
 * Shared: used by roadmap attachments and by shake-report screenshots.
 *
 * Hand-rolled rather than pulling in a lightbox package: the whole
 * requirement is one image, a backdrop and two ways to dismiss, and every
 * library in that space ships a carousel, zoom and thumbnail strip we'd never
 * use. Deliberately NOT the shared `Modal` — that one is a light panel sized
 * for forms, and an image viewer wants a dark full-bleed backdrop.
 */
import { useEffect } from 'react';

interface ImageLightboxProps {
  /** null ⇒ closed. */
  src: string | null;
  /** Shown bottom-left and used as the image's alt text. */
  filename?: string;
  onClose: () => void;
}

export function ImageLightbox({
  src,
  filename,
  onClose,
}: ImageLightboxProps) {
  // Bound to the document, not the overlay: the overlay would need focus to
  // see the key, and nothing here is focusable.
  //
  // CAPTURE phase, and it swallows the event. The parent `Modal` also listens
  // for Escape on document — in the bubble phase, calling
  // stopImmediatePropagation — and it registered first, so a bubble-phase
  // listener here would never run and Escape would close the whole editor
  // instead of just the preview. Capturing at document beats both.
  useEffect(() => {
    if (!src) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={filename ? `Preview of ${filename}` : 'Image preview'}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Stop propagation so clicking the image itself doesn't dismiss. */}
      <img
        src={src}
        alt={filename ?? 'Attachment preview'}
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded object-contain shadow-2xl"
      />

      {filename && (
        <p className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 truncate rounded bg-black/60 px-3 py-1 text-sm text-white">
          {filename}
        </p>
      )}
    </div>
  );
}
